/** Canvas-pixel rectangle. Used to keep labels off the HUD. */
export interface ScreenRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** World-pixel distance from the view top to the boss bar, before any HUD lift. */
export const BOSS_BAR_OFFSET = 28;
/**
 * Outlined "BOSS" label sits this many world pixels above the bar.
 * SCALE is 2: seven units of lead plus one unit of outline.
 */
export const BOSS_LABEL_ABOVE = 16;

export function overlapsScreen(a: ScreenRect, b: ScreenRect, gap: number): boolean {
  return a.x < b.x + b.w + gap && a.x + a.w + gap > b.x && a.y < b.y + b.h + gap && a.y + a.h + gap > b.y;
}

export function hitsScreen(box: ScreenRect, obstacles: readonly ScreenRect[], gap: number): boolean {
  return obstacles.some((item) => overlapsScreen(box, item, gap));
}

/** Portal domain label. `boxH` is the fill; the outline adds 2px on every side. */
export function labelFrame(x: number, y: number, textW: number, padX: number, boxH: number): ScreenRect {
  const left = Math.round(x - textW / 2 - padX) - 2;
  const top = Math.round(y - boxH) - 2;
  return { x: left, y: top, w: Math.ceil(textW) + padX * 2 + 4, h: boxH + 4 };
}

/**
 * Keep a baseline-bottom label on screen and off the HUD.
 * The label sits at `aboveY` when that box is clear. Otherwise it flips to
 * `belowY`, then drops under the HUD if that still collides.
 */
export function placeBaselineLabel(
  sx: number,
  aboveY: number,
  belowY: number,
  viewW: number,
  viewH: number,
  textW: number,
  padX: number,
  boxH: number,
  gap: number,
  hud: readonly ScreenRect[],
): { x: number; y: number } {
  const frameW = Math.ceil(textW) + padX * 2 + 4;
  const minX = gap + frameW / 2;
  const maxX = viewW - gap - frameW / 2;
  const x = maxX >= minX ? Math.min(maxX, Math.max(minX, sx)) : viewW / 2;
  const at = (y: number) => labelFrame(x, y, textW, padX, boxH);
  const onScreen = (box: ScreenRect) => box.y >= gap && box.y + box.h <= viewH - gap;
  let y = aboveY;
  let box = at(y);
  if (box.y < gap) {
    y += gap - box.y;
    box = at(y);
  }
  if (onScreen(box) && !hitsScreen(box, hud, gap)) return { x, y };

  y = belowY;
  box = at(y);
  if (hitsScreen(box, hud, gap)) {
    let floor = 0;
    for (const item of hud) {
      if (overlapsScreen(box, item, gap)) floor = Math.max(floor, item.y + item.h);
    }
    y = floor + gap + boxH + 2;
    box = at(y);
  }
  if (box.y + box.h > viewH - gap) {
    const lifted = y - (box.y + box.h - (viewH - gap));
    const liftedBox = at(lifted);
    if (!hitsScreen(liftedBox, hud, gap)) {
      y = lifted;
      box = liftedBox;
    }
  }
  if (box.y < gap) {
    const dropped = y + (gap - box.y);
    if (!hitsScreen(at(dropped), hud, gap)) y = dropped;
  }
  return { x, y };
}

/** Move a center-anchored label down until its box clears the HUD. */
export function clearCenterLabel(
  lx: number,
  ly: number,
  boxW: number,
  boxH: number,
  viewH: number,
  gap: number,
  hud: readonly ScreenRect[],
): number {
  const frame = (y: number): ScreenRect => ({
    x: Math.round(lx - boxW / 2) - 2,
    y: Math.round(y - boxH / 2) - 2,
    w: boxW + 4,
    h: boxH + 4,
  });
  if (!hitsScreen(frame(ly), hud, gap)) return ly;
  let floor = 0;
  for (const item of hud) {
    if (overlapsScreen(frame(ly), item, gap)) floor = Math.max(floor, item.y + item.h);
  }
  const below = floor + gap + boxH / 2 + 2;
  const clamped = Math.max(boxH / 2 + gap, Math.min(viewH - boxH / 2 - gap, below));
  if (hitsScreen(frame(clamped), hud, gap)) return below;
  return clamped;
}

/** Extra world pixels so the boss bar and its label sit under a measured HUD. */
export function bossBarLift(hudBottom: number, pixelScale: number, worldZ: number): number {
  if (!(hudBottom > 0)) return 0;
  const gap = 4 * Math.max(1, pixelScale);
  const needed = (hudBottom + gap) / Math.max(0.01, worldZ) + BOSS_LABEL_ABOVE;
  return Math.max(0, needed - BOSS_BAR_OFFSET);
}
